"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { LOCALE_STORAGE_KEY } from "@/components/providers/preference-bootstrap";
import {
  DEFAULT_LOCALE,
  getDictionary,
  getDirection,
  getOppositeLocale,
  isLocale,
  type Dictionary,
  type Direction,
  type Locale,
} from "@/lib/i18n";

export { LOCALE_STORAGE_KEY };

export interface LocaleContextValue {
  locale: Locale;
  dir: Direction;
  /** The active dictionary — every user-visible string is read from it. */
  t: Dictionary;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
}

/**
 * Exported so a subtree can be PINNED to one language without mounting a second
 * `LocaleProvider` — the formal Arabic report does exactly that. Providing a value directly
 * runs no effect, so it never touches `<html>` and cannot fight the shell's own provider.
 */
export const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * The locale lives in `localStorage`, not in React state: it must survive
 * reloads, stay identical across the tabs an ERP is habitually opened in, and be
 * readable by the bootstrap script below before React exists. `useSyncExternal-
 * Store` is what lets a component subscribe to that without a setState effect.
 */
const listeners = new Set<() => void>();

/** Keeps the choice usable when storage is blocked (private mode, sandboxed iframe). */
let memoryLocale: Locale | null = null;

function readLocale(): Locale {
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(raw)) return raw;
  } catch {
    // Fall through to the in-memory value.
  }
  return memoryLocale ?? DEFAULT_LOCALE;
}

function writeLocale(locale: Locale): void {
  memoryLocale = locale;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // The choice still holds for this tab.
  }
  // `storage` events fire in *other* tabs only, so this tab is notified here.
  for (const listener of listeners) listener();
}

function subscribeToLocale(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === LOCALE_STORAGE_KEY) onStoreChange();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener("storage", onStorage);
  };
}

/** The server cannot know the stored choice; Arabic is the product default. */
function readServerLocale(): Locale {
  return DEFAULT_LOCALE;
}

function applyLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = getDirection(locale);
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  // Hydration renders with the server snapshot, then React immediately re-reads
  // the stored value — the bootstrap script has already fixed `dir` by then.
  const locale = useSyncExternalStore(subscribeToLocale, readLocale, readServerLocale);

  useEffect(() => {
    applyLocale(locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    writeLocale(next);
  }, []);

  const toggleLocale = useCallback(() => {
    writeLocale(getOppositeLocale(locale));
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      dir: getDirection(locale),
      t: getDictionary(locale),
      setLocale,
      toggleLocale,
    }),
    [locale, setLocale, toggleLocale],
  );

  // No bootstrap script here: it is rendered once from the root layout's
  // `<head>` by `PreferenceBootstrap`, which is early enough to beat the paint.
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) {
    throw new Error("useLocale must be used inside <LocaleProvider>.");
  }
  return value;
}

/**
 * Mounts a `LocaleProvider` only when the tree does not already have one. Two
 * nested providers would fight over `<html>` from separate effects, so
 * route-group layouts use this and stay correct whether or not the root layout
 * already provides the locale. Mirrors `EnsureAuthProvider`.
 */
export function EnsureLocaleProvider({ children }: { children: ReactNode }) {
  const existing = useContext(LocaleContext);
  if (existing) return <>{children}</>;
  return <LocaleProvider>{children}</LocaleProvider>;
}
